import { WebRtcFileTransfer } from '../webrtc/web-rtc-file-transfer';
import { PreviewRtcPeerConnection } from '../webrtc/preview-rtc-peer-connection';
/**
 * Manages communication with one preview guest.
 */
export class PreviewRtcGuestConnection extends PreviewRtcPeerConnection {
    constructor(signalingChannel, connection, scope, ownClientId, peerId, eventListener, role) {
        super(signalingChannel, connection, ownClientId, peerId, role);
        this.scope = scope;
        this.eventListener = eventListener;
        this.isInitialized = false;
        // The application bundle to be transferred
        this.pendingBundle = null;
        // Data channels
        this.messageChannel = null;
        this.messageChannelIsOpen = false;
        this.fileChannel = null;
        this.fileChannelIsOpen = false;
        // Promises
        this.initPromise = null;
        this.pendingPreviewPromise = null;
    }
    /**
     * Initializes the connection by setting up data channels and sending an offer.
     * This function returns a promise which resolves when the data channels are ready
     * to use.
     */
    init() {
        if (!this.canUseWebRTC) {
            this.isInitialized = true;
            return Promise.resolve();
        }
        // Wait for the onnegotiationneeded event to create an offer. The event
        // will not fire until we create data channels.
        this.connection.onnegotiationneeded = (event) => {
            console.log(`${this.role}: received onnegotiationneeded event from ${this.peerId}. Sending offer...`);
            const options = {}; // no audio/video used, just data
            this.sendOffer(options);
        };
        return new Promise((resolve, reject) => {
            // So:
            // 1) create data channels
            // 2) onnegotiationneeded above will be called, which will
            // 3) send the offer
            this.createPreviewDataChannels();
            this.initPromise = { resolve: resolve, reject: reject };
        });
    }
    // public logDataChannelState(): void {
    //     this.messageChannel
    // }
    createPreviewDataChannels() {
        this.createDataChannel("preview-message-channel" /* DataChannels.PreviewMessageChannelLabel */);
        this.createDataChannel("preview-file-channel" /* DataChannels.PreviewFileChannelLabel */, "arraybuffer" /* DataChannels.PreviewFileChannelBinaryType */);
    }
    handleDataChannelCreated(dataChannel, isInitiator) {
        super.handleDataChannelCreated(dataChannel, isInitiator);
        switch (dataChannel.label) {
            case "preview-message-channel" /* DataChannels.PreviewMessageChannelLabel */:
                // Subscribe to message channel messages
                this.messageChannel = dataChannel;
                dataChannel.onmessage = (ev) => {
                    let message;
                    try {
                        message = JSON.parse(ev.data);
                    }
                    catch (e) {
                        return console.error(e);
                    }
                    this.handlePreviewMessage(message);
                };
                break;
            case "preview-file-channel" /* DataChannels.PreviewFileChannelLabel */:
                this.fileChannel = dataChannel;
                break;
        }
    }
    handlePeerMessage(message) {
        switch (message.type) {
            case 11 /* SignalingMessageType.PreviewFallback */:
                this.handlePreviewMessage(message.body);
                break;
        }
    }
    handlePreviewMessage(message) {
        switch (message.type) {
            case 'ack-preview':
                this.handleAcknowledgePreviewMessage(message);
                break;
        }
        this.eventListener.onReceivedGuestMessage(this, message);
    }
    /**
     * Checks if the data channel message is an AcknowledgePreviewMessage and if so, starts
     * file transfer of the preview to the guest.
     */
    handleAcknowledgePreviewMessage(ackMessage) {
        // TODO: we could verify the chunk and hash
        // The guest has accepted our preview. Start file transfer.
        let transferPromise;
        if (!ackMessage.accept) {
            // Not accepted, that's ok: continue
            transferPromise = Promise.resolve();
        }
        else if (!this.pendingBundle) {
            // Accepted but no bundle
            transferPromise = Promise.reject(`${this.role}: received '${ackMessage.type}' message but there is no update pending.`);
        }
        else if (this.canUseWebRTC) {
            // Transfer over WebRTC
            if (!this.fileChannel) {
                transferPromise = Promise.reject(`${this.role}: Received '${ackMessage.type}' message but there is no file channel.`);
            }
            else {
                transferPromise = WebRtcFileTransfer.sendBundle(this.pendingBundle, ackMessage.dirtyOnly, this.fileChannel);
            }
        }
        else {
            // Transfer over the signaling channel
            const ft = this.signalingChannel.createFileTransferFallback();
            transferPromise = ft.sendBundle(this.pendingBundle, ackMessage.dirtyOnly, this.peerId);
        }
        transferPromise
            .then(() => {
            if (this.pendingPreviewPromise)
                this.pendingPreviewPromise.resolve();
        })
            .catch((reason) => {
            if (this.pendingPreviewPromise)
                this.pendingPreviewPromise.reject(reason);
            else
                throw reason;
        }).finally(() => {
            this.pendingBundle = null; // dispose the update
            this.pendingPreviewPromise = null;
        });
    }
    handleDataChannelOpen(dataChannel) {
        super.handleDataChannelOpen(dataChannel);
        // console.log(`PreviewHost: data channel ${dataChannel.label} is open.`);
        switch (dataChannel.label) {
            case "preview-message-channel" /* DataChannels.PreviewMessageChannelLabel */:
                this.messageChannelIsOpen = true;
                break;
            case "preview-file-channel" /* DataChannels.PreviewFileChannelLabel */:
                this.fileChannelIsOpen = true;
                break;
        }
        // If both channels are open and we are initializing, resolve
        if (this.messageChannelIsOpen && this.fileChannelIsOpen && this.initPromise) {
            // console.log(`PreviewHost: both data channels are open. Resolving initialization.`);
            this.initPromise.resolve();
            this.initPromise = null;
            this.isInitialized = true;
        }
    }
    handleDataChannelClose(dataChannel) {
        super.handleDataChannelClose(dataChannel);
        switch (dataChannel.label) {
            case "preview-message-channel" /* DataChannels.PreviewMessageChannelLabel */:
                this.messageChannel = null;
                this.messageChannelIsOpen = false;
                break;
            case "preview-file-channel" /* DataChannels.PreviewFileChannelLabel */:
                this.fileChannel = null;
                this.fileChannelIsOpen = false;
                break;
        }
    }
    sendMessage(message) {
        if (!this.canUseWebRTC) {
            const fallback = { type: 11 /* SignalingMessageType.PreviewFallback */, body: message, fromClientId: this.clientId, toClientId: this.peerId };
            return this.signalingChannel.sendPeerMessage(fallback);
        }
        if (!this.messageChannelIsOpen) {
            throw `${this.role}: unable to send message to ${this.peerId}. The message channel is closed'.`;
        }
        if (!this.messageChannel) {
            throw `${this.role}: unable to send message to ${this.peerId}. The message channel is ${this.messageChannel}'.`;
        }
        if (this.messageChannel.readyState !== 'open') {
            throw `${this.role}: unable to send message to ${this.peerId}. The message channel is ${this.messageChannel.readyState}'.`;
        }
        this.messageChannel.send(JSON.stringify(message));
    }
    sendPreviewBundle(hasPreviewMessage, bundle) {
        if (this.isDisposed)
            return Promise.resolve();
        if (!this.isInitialized)
            return Promise.reject(`${this.role}: unable to send message to ${this.peerId}. The connection is not initialized'.`);
        if (this.canUseWebRTC) {
            if (!this.fileChannel) {
                return Promise.reject(`${this.role}: unable to send bundle to ${this.peerId}. The file channel is null'.`);
            }
            if (this.fileChannel.readyState !== 'open') {
                return Promise.reject(`${this.role}: unable to send bundle to ${this.peerId}. The file channel state is ${this.fileChannel.readyState}'.`);
            }
        }
        // Keep the pending update until we got an acknowledgement in handleDataChannelMessage
        this.pendingBundle = bundle;
        const promise = new Promise((resolve, reject) => {
            this.sendMessage(hasPreviewMessage);
            this.pendingPreviewPromise = { resolve: resolve, reject: reject };
        });
        return promise;
    }
    dispose() {
        super.dispose();
        if (this.messageChannel) {
            this.messageChannel.close();
            this.messageChannel = null;
        }
        if (this.fileChannel) {
            this.fileChannel.close();
            this.fileChannel = null;
        }
    }
}
//# sourceMappingURL=preview-rtc-guest-connection.js.map